import React, { useEffect, useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tooltip,
  Typography,
  MenuItem,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  Divider,
  Menu,
  FormControl,
  InputLabel,
  Select,
  InputAdornment
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { collection, query, orderBy, onSnapshot, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { sampleTournaments } from '../data/tournamentHistory';

function TournamentList() {
  const { currentUser } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [error, setError] = useState(null);
  const [editingTournament, setEditingTournament] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [viewingNotes, setViewingNotes] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState({
    gameType: 'all',
    tournamentName: 'all'
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });
  const [loading, setLoading] = useState(true);

  const gameTypes = [
    { value: 'LIVE_MTT', label: 'Live MTT' },
    { value: 'ONLINE_MTT', label: 'Online MTT' },
    { value: 'LIVE_CASH', label: 'Live Cash' },
    { value: 'ONLINE_CASH', label: 'Online Cash' }
  ];

  useEffect(() => {
    if (!currentUser) return;
    
    const tournamentsRef = collection(db, 'tournaments');
    const q = query(
      tournamentsRef, 
      where('userId', '==', currentUser.uid),
      orderBy('date', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tournamentData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTournaments(tournamentData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching tournaments:", error);
      setError("Failed to load tournaments");
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [currentUser]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'R0';
    return `R${Math.abs(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    
    try {
      // Handle Firebase Timestamp objects
      let date;
      if (dateString.toDate) {
        date = dateString.toDate();
      } else {
        // Handle string dates
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      // Format as "9 Mar 2025"
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Invalid Date';
    }
  };

  const calculateProfit = (tournament) => {
    if (!tournament) return 0;
    
    try {
      const buyin = Number(tournament.buyin) || 0;
      const result = Number(tournament.result) || 0;
      
      if (tournament.gameType === 'live-mtt' || tournament.gameType === 'online-mtt') {
        const rebuys = Number(tournament.rebuys) || 0;
        const rebuyAmount = Number(tournament.rebuyAmount) || 0;
        const addons = Number(tournament.addons) || 0;
        const addonAmount = Number(tournament.addonAmount) || 0;
        
        return result - (buyin + (rebuys * rebuyAmount) + (addons * addonAmount));
      } else {
        return result - buyin;
      }
    } catch (error) {
      console.error("Error calculating profit:", error);
      return 0;
    }
  };

  const calculateTotalRebuyAmount = (tournament) => {
    if (!tournament) return 0;
    const rebuys = Number(tournament.rebuys) || 0;
    const rebuyAmount = Number(tournament.rebuyAmount) || 0;
    return rebuys * rebuyAmount;
  };

  const calculateTotalAddonAmount = (tournament) => {
    if (!tournament) return 0;
    const addons = Number(tournament.addons) || 0;
    const addonAmount = Number(tournament.addonAmount) || 0;
    return addons * addonAmount;
  };

  const handleEdit = (tournament) => {
    // Convert the date string to a dayjs object
    const formattedDate = tournament.date ? 
      (typeof tournament.date === 'string' ? dayjs(tournament.date) : dayjs(tournament.date.toDate())) : 
      dayjs();
    
    setEditingTournament(tournament);
    setEditFormData({
      ...tournament,
      date: formattedDate
    });
    setOpenDialog(true);
  };

  const handleDelete = async (tournamentId) => {
    if (window.confirm('Are you sure you want to delete this tournament?')) {
      try {
        await deleteDoc(doc(db, 'tournaments', tournamentId));
      } catch (error) {
        console.error('Error deleting tournament:', error);
        setError('Failed to delete tournament');
      }
    }
  };

  const handleEditSubmit = async () => {
    try {
      const tournamentRef = doc(db, 'tournaments', editingTournament.id);
      
      // Calculate profit based on game type
      let profit = calculateProfit(editFormData);
      
      // Format date properly for Firebase
      let formattedDate;
      if (editFormData.date && typeof editFormData.date.format === 'function') {
        formattedDate = editFormData.date.format('YYYY-MM-DD');
      } else {
        formattedDate = editFormData.date;
      }
      
      const updatedData = {
        ...editFormData,
        buyin: Number(editFormData.buyin),
        rebuys: Number(editFormData.rebuys || 0),
        rebuyAmount: Number(editFormData.rebuyAmount || 0),
        addons: Number(editFormData.addons || 0),
        addonAmount: Number(editFormData.addonAmount || 0),
        result: Number(editFormData.result),
        profit: profit,
        date: formattedDate
      };
      
      await updateDoc(tournamentRef, updatedData);
      setOpenDialog(false);
    } catch (error) {
      console.error('Error updating tournament:', error);
      setError('Failed to update tournament');
    }
  };

  const handleChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (newDate) => {
    setEditFormData({
      ...editFormData,
      date: newDate
    });
  };

  const renderNotes = (notes) => {
    if (!notes) return '-';
    
    if (notes.length <= 25) {
      return notes;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={notes}>
          <span>{notes.substring(0, 25)}...</span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => setViewingNotes(notes)}
          sx={{ ml: 1 }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  // Filter the tournaments based on selected filters
  const filteredTournaments = tournaments.filter(tournament => {
    return Object.entries(filters).every(([key, value]) => {
      if (value === 'all') return true;
      return tournament[key] === value;
    });
  });

  // Get unique values for filter options
  const getFilterOptions = (field) => {
    const options = new Set(tournaments.map(t => t[field]).filter(Boolean));
    return ['all', ...Array.from(options)];
  };

  // First, let's create a styled chip for game types
  const GameTypeChip = ({ gameType }) => {
    // Color schemes for different game types
    const colorSchemes = {
      'LIVE_MTT': {
        bg: 'rgba(147, 51, 234, 0.1)',  // Purple
        color: '#9333ea',
        border: 'rgba(147, 51, 234, 0.2)'
      },
      'ONLINE_MTT': {
        bg: 'rgba(59, 130, 246, 0.1)',   // Blue
        color: '#3b82f6',
        border: 'rgba(59, 130, 246, 0.2)'
      },
      'LIVE_CASH': {
        bg: 'rgba(34, 197, 94, 0.1)',    // Green
        color: '#22c55e',
        border: 'rgba(34, 197, 94, 0.2)'
      },
      'ONLINE_CASH': {
        bg: 'rgba(249, 115, 22, 0.1)',   // Orange
        color: '#f97316',
        border: 'rgba(249, 115, 22, 0.2)'
      }
    };

    const colors = colorSchemes[gameType] || {
      bg: 'rgba(100, 116, 139, 0.1)',    // Default gray
      color: '#64748b',
      border: 'rgba(100, 116, 139, 0.2)'
    };

    return (
      <Box
        sx={{
          display: 'inline-flex',
          backgroundColor: colors.bg,
          color: colors.color,
          borderRadius: '6px',
          padding: '4px 8px',
          fontSize: '0.75rem',
          fontWeight: 500,
          border: `1px solid ${colors.border}`
        }}
      >
        {gameType?.replace('_', ' ') || '-'}
      </Box>
    );
  };

  // Update the TableHead section in DesktopView
  const FilterableHeader = ({ column, label }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [localSearchText, setLocalSearchText] = useState('');

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
      setLocalSearchText('');
    };

    const handleFilterSelectLocal = (value) => {
      setFilters(prev => ({
        ...prev,
        [column]: value
      }));
      handleClose();
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'inherit' }}>
        {label}
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ 
            color: filters[column] === 'all' ? '#e2e8f0' : '#9333ea',
            opacity: filters[column] === 'all' ? 0.5 : 1,
            '&:hover': { 
              opacity: 1,
              backgroundColor: 'rgba(147, 51, 234, 0.1)' // Purple with low opacity
            }
          }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ p: 1, width: 200 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={localSearchText}
              onChange={(e) => setLocalSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{ mb: 1 }}
            />
            {getFilterOptions(column)
              .filter(option => 
                option.toLowerCase().includes(localSearchText.toLowerCase())
              )
              .map((option) => (
                <MenuItem 
                  key={option}
                  onClick={() => handleFilterSelectLocal(option)}
                  selected={filters[column] === option}
                  sx={{ width: '100%' }}
                >
                  {option === 'all' ? 'Show All' : option.replace('_', ' ')}
                </MenuItem>
              ))
            }
          </Box>
        </Menu>
      </Box>
    );
  };

  // Mobile card view
  const MobileView = () => (
    <Box sx={{ mb: 2 }}>
      {tournaments.map((tournament) => (
        <Card key={tournament.id} sx={{ mb: 2, bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" component="div">
                {tournament.tournamentName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(tournament.date)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={tournament.gameType?.replace('_', ' ') || '-'} 
                size="small" 
                sx={{ bgcolor: 'primary.dark', color: 'white' }}
              />
              {tournament.gameType?.includes('MTT') && (
                <>
                  <Chip 
                    label={tournament.fieldSize ? `${tournament.fieldSize} players` : '-'} 
                    size="small" 
                    sx={{ bgcolor: 'secondary.dark', color: 'white' }}
                  />
                  <Chip 
                    label={tournament.place ? `Place ${tournament.place}` : '-'} 
                    size="small" 
                    sx={{ bgcolor: 'secondary.dark', color: 'white' }}
                  />
                </>
              )}
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Buy-in</Typography>
                <Typography variant="body1">{formatCurrency(tournament.buyIn)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Cash Out</Typography>
                <Typography variant="body1">{formatCurrency(tournament.winnings)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Result</Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: tournament.profit >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 'bold'
                  }}
                >
                  {formatCurrency(tournament.profit)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Add this function to handle clearing all filters and sorts
  const handleClearAll = () => {
    setFilters({
      gameType: 'all',
      tournamentName: 'all'
    });
    setSortConfig({
      key: 'date',
      direction: 'desc'
    });
  };

  // Desktop table view
  const DesktopView = () => (
    <>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mb: 2 
      }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearAll}
          startIcon={<FilterListIcon />}
          sx={{
            borderColor: 'grey.400',
            color: 'grey.600',
            '&:hover': {
              borderColor: 'grey.500',
              backgroundColor: 'grey.100'
            }
          }}
        >
          Clear All Filters & Sorting
        </Button>
      </Box>
      <TableContainer 
        component={Paper} 
        sx={{ 
          userSelect: 'none',  // Prevent text selection
          WebkitUserSelect: 'none',  // For Safari
          MozUserSelect: 'none',  // For Firefox
          msUserSelect: 'none',  // For IE/Edge
          backgroundColor: 'transparent',  // Remove white background
          '& .MuiTable-root': {
            backgroundColor: 'transparent'  // Remove white background from table
          }
        }}
      >
        <Table sx={{ 
          '& td, & th': {
            userSelect: 'none',
            cursor: 'default'
          },
          '& thead': {
            '& tr': {
              '& th': {
                backgroundColor: '#1e293b', // Tailwind slate-800
                color: '#e2e8f0',          // Tailwind slate-200
                fontWeight: 600,
                fontSize: '0.875rem',
                padding: '12px 16px',
                borderBottom: 'none',
                '&:first-of-type': {
                  borderTopLeftRadius: 8,
                },
                '&:last-child': {
                  borderTopRightRadius: 8,
                }
              }
            }
          },
          '& tbody tr': {
            '&:hover': {
              backgroundColor: 'rgba(148, 163, 184, 0.05)' // Tailwind slate-400 with low opacity
            },
            '& td': {
              borderBottom: '1px solid #334155', // Tailwind slate-700
              padding: '12px 16px'
            }
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <SortableHeader column="date" label="Date" />
              </TableCell>
              <TableCell>
                <SortableHeader column="gameType" label="Game Type" />
              </TableCell>
              <TableCell>Tournament/Game</TableCell>
              <TableCell>Number of Players</TableCell>
              <TableCell>
                <SortableHeader column="finalRanking" label="Final Ranking" />
              </TableCell>
              <TableCell>
                <SortableHeader column="buyin" label="Buy-in Amount" />
              </TableCell>
              <TableCell>Rebuy Amount</TableCell>
              <TableCell>Add-on Amount</TableCell>
              <TableCell>
                <SortableHeader column="result" label="Winnings" />
              </TableCell>
              <TableCell>
                <SortableHeader column="profit" label="Profit/Loss" />
              </TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortTournaments(tournaments).map((tournament) => (
              <TableRow key={tournament.id}>
                <TableCell>{formatDate(tournament.date)}</TableCell>
                <TableCell>{getGameTypeLabel(tournament.gameType)}</TableCell>
                <TableCell>{tournament.name}</TableCell>
                <TableCell>
                  {tournament.playerRange === 'specify' 
                    ? tournament.exactPlayers 
                    : tournament.players || tournament.playerRange || 'N/A'}
                </TableCell>
                <TableCell>
                  {tournament.finalRanking ? `#${tournament.finalRanking}` : '-'}
                </TableCell>
                <TableCell>{formatCurrency(tournament.buyin)}</TableCell>
                <TableCell>{formatCurrency(calculateTotalRebuyAmount(tournament))}</TableCell>
                <TableCell>{formatCurrency(calculateTotalAddonAmount(tournament))}</TableCell>
                <TableCell>{formatCurrency(tournament.result)}</TableCell>
                <TableCell 
                  sx={{ 
                    color: calculateProfit(tournament) >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 'bold'
                  }}
                >
                  {formatCurrency(calculateProfit(tournament))}
                </TableCell>
                <TableCell>
                  {tournament.notes ? (
                    <Tooltip title="View Notes">
                      <IconButton onClick={() => setViewingNotes(tournament.notes)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(tournament)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(tournament.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const sortTournaments = (tournaments) => {
    if (!sortConfig.key) return tournaments;
    
    return [...tournaments].sort((a, b) => {
      // Handle different data types for sorting
      switch (sortConfig.key) {
        case 'date':
          // Convert dates to timestamps for comparison
          const dateA = a.date ? (a.date.toDate ? a.date.toDate() : new Date(a.date)) : new Date(0);
          const dateB = b.date ? (b.date.toDate ? b.date.toDate() : new Date(b.date)) : new Date(0);
          return sortConfig.direction === 'asc' 
            ? dateA.getTime() - dateB.getTime() 
            : dateB.getTime() - dateA.getTime();
        
        case 'gameType':
          // Sort by game type label
          const typeA = getGameTypeLabel(a.gameType);
          const typeB = getGameTypeLabel(b.gameType);
          return sortConfig.direction === 'asc'
            ? typeA.localeCompare(typeB)
            : typeB.localeCompare(typeA);
        
        case 'finalRanking':
          // Sort by final ranking (numerically)
          const rankA = Number(a.finalRanking) || Number.MAX_SAFE_INTEGER; // Put empty rankings at the end
          const rankB = Number(b.finalRanking) || Number.MAX_SAFE_INTEGER;
          return sortConfig.direction === 'asc' ? rankA - rankB : rankB - rankA;
        
        case 'buyin':
          // Sort by buy-in amount (numerically)
          const buyinA = Number(a.buyin) || 0;
          const buyinB = Number(b.buyin) || 0;
          return sortConfig.direction === 'asc' ? buyinA - buyinB : buyinB - buyinA;
        
        case 'result':
          // Sort by winnings (numerically)
          const resultA = Number(a.result) || 0;
          const resultB = Number(b.result) || 0;
          return sortConfig.direction === 'asc' ? resultA - resultB : resultB - resultA;
        
        case 'profit':
          // Sort by profit (numerically)
          const profitA = calculateProfit(a);
          const profitB = calculateProfit(b);
          return sortConfig.direction === 'asc' ? profitA - profitB : profitB - profitA;
        
        default:
          // Default sorting for other columns
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
      }
    });
  };

  const handleSort = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
    setSortConfig(prevConfig => ({
      key: property,
      direction: isAsc ? 'desc' : 'asc'
    }));
  };

  const SortableHeader = ({ column, label }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        userSelect: 'none',
        color: 'inherit',
        '&:hover': { 
          opacity: 0.8 
        }
      }}
      onClick={() => handleSort(column)}
    >
      {label}
      {sortConfig.key === column ? (
        sortConfig.direction === 'asc' ? 
          <ArrowUpwardIcon fontSize="small" sx={{ color: '#9333ea' }} /> : 
          <ArrowDownwardIcon fontSize="small" sx={{ color: '#9333ea' }} />
      ) : (
        <ArrowUpwardIcon 
          fontSize="small" 
          sx={{ 
            opacity: 0.3,
            color: '#e2e8f0',
            '&:hover': { opacity: 0.7 }
          }} 
        />
      )}
    </Box>
  );

  // Add a helper function to get the game type label in uppercase
  const getGameTypeLabel = (gameType) => {
    const gameTypeObj = gameTypes.find(type => type.value === gameType);
    return gameTypeObj ? gameTypeObj.label.toUpperCase() : gameType.toUpperCase();
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box>
        {tournaments.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', my: 4 }}>
            No tournaments recorded yet. Add your first tournament to get started!
          </Typography>
        ) : (
          isMobile ? <MobileView /> : <DesktopView />
        )}
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Tournament</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            fullWidth
            label="Tournament/Game Name *"
            name="name"
            value={editFormData.name || ''}
            onChange={handleChange}
            required
            sx={{ mb: 2, mt: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="game-type-label">Game Type *</InputLabel>
            <Select
              labelId="game-type-label"
              name="gameType"
              value={editFormData.gameType || ''}
              onChange={handleChange}
              required
            >
              <MenuItem value="live-mtt">Live MTT (Tournament)</MenuItem>
              <MenuItem value="online-mtt">Online MTT (Tournament)</MenuItem>
              <MenuItem value="live-cash">Live Cash Game</MenuItem>
              <MenuItem value="online-cash">Online Cash Game</MenuItem>
            </Select>
          </FormControl>
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date *"
              value={editFormData.date ? dayjs(editFormData.date) : null}
              onChange={(newDate) => {
                setEditFormData({
                  ...editFormData,
                  date: newDate
                });
              }}
              renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
              sx={{ mb: 2, width: '100%' }}
            />
          </LocalizationProvider>
          
          {(editFormData.gameType === 'live-mtt' || editFormData.gameType === 'online-mtt') && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="player-range-label">Player Count Range</InputLabel>
              <Select
                labelId="player-range-label"
                name="playerRange"
                value={editFormData.playerRange || '15-30'}
                onChange={handleChange}
              >
                <MenuItem value="15-30">15-30 players</MenuItem>
                <MenuItem value="31-50">31-50 players</MenuItem>
                <MenuItem value="51-80">51-80 players</MenuItem>
                <MenuItem value="81-100">81-100 players</MenuItem>
                <MenuItem value="101-150">101-150 players</MenuItem>
                <MenuItem value="specify">Specify exact number</MenuItem>
              </Select>
            </FormControl>
          )}
          
          {editFormData.playerRange === 'specify' && (
            <TextField
              fullWidth
              label="Exact Player Count"
              name="exactPlayers"
              type="number"
              value={editFormData.exactPlayers || ''}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          )}
          
          <TextField
            fullWidth
            type="number"
            label="Buy-in Amount *"
            name="buyin"
            value={editFormData.buyin || ''}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">R</InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />
          
          {(editFormData.gameType === 'live-mtt' || editFormData.gameType === 'online-mtt') && (
            <>
              <TextField
                fullWidth
                label="Number of Rebuys *"
                name="rebuys"
                type="number"
                value={editFormData.rebuys || ''}
                onChange={handleChange}
                required
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Rebuy Amount *"
                name="rebuyAmount"
                type="number"
                value={editFormData.rebuyAmount || ''}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">R</InputAdornment>,
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Number of Add-ons *"
                name="addons"
                type="number"
                value={editFormData.addons || ''}
                onChange={handleChange}
                required
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Add-on Amount *"
                name="addonAmount"
                type="number"
                value={editFormData.addonAmount || ''}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">R</InputAdornment>,
                }}
                sx={{ mb: 2 }}
              />
            </>
          )}
          
          <TextField
            fullWidth
            type="number"
            label="Result (+ for win, - for loss) *"
            name="result"
            value={editFormData.result || ''}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">R</InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            multiline
            rows={3}
            value={editFormData.notes || ''}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {(editFormData.gameType === 'live-mtt' || editFormData.gameType === 'online-mtt') && (
            <TextField
              fullWidth
              label="Final Ranking (optional)"
              name="finalRanking"
              type="number"
              value={editFormData.finalRanking || ''}
              onChange={handleChange}
              placeholder="e.g., 3 for 3rd place"
              InputProps={{
                startAdornment: <InputAdornment position="start">#</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={Boolean(viewingNotes)} 
        onClose={() => setViewingNotes(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Notes</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
            {viewingNotes}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingNotes(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TournamentList; 